<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Select from 'primevue/select'

import { pageTabsOf, sectionOf, visibleNavigation } from '@/router/navigation'
import { useAuth } from '@/stores/auth'
import { useCompanies } from '@/stores/companies'

const route = useRoute()
const router = useRouter()
const auth = useAuth()
const companies = useCompanies()

/** „Всички дружества“ стои първа; Select не държи `null` като стойност, затова 0. */
const ALL_COMPANIES = 0
const companyOptions = computed(() => [
  { id: ALL_COMPANIES, name: 'Всички дружества' },
  ...companies.options,
])
function pickCompany(id: number | null): void {
  companies.setActive(id === ALL_COMPANIES || id === null ? null : id)
}

const section = computed(() => sectionOf(route.name))
// Страница със свои раздели (нарядът) ги слага в горната лента вместо тези на раздела.
const tabs = computed(() => route.meta.ownTabs ? undefined : pageTabsOf(section.value, visibleNavigation(auth)))

async function signOut(): Promise<void> {
  await auth.logout()
  companies.reset()
  await router.replace({ name: 'login' })
}
</script>

<template>
  <header class="topbar">
    <nav v-if="tabs" class="topbar__tabs" :aria-label="tabs.label">
      <RouterLink
        v-for="tab in tabs.tabs"
        :key="tab.name"
        :to="{ name: tab.name }"
        class="topbar__tab"
        :class="{ 'topbar__tab--active': section === tab.name }"
        :aria-current="section === tab.name ? 'page' : undefined"
      >
        <i :class="tab.icon" aria-hidden="true" />
        {{ tab.label }}
      </RouterLink>
    </nav>
    <!-- A record page puts its own tabs here (the наряд: Наряд · Попълнено · Връзки). -->
    <div v-else id="topbar-page-tabs" class="topbar__tabs" />

    <div class="topbar__right">
      <a v-if="auth.can('projects')" href="/m/obekti" class="topbar__mobile">Мобилна версия</a>
      <Select
        v-if="!auth.restricted"
        :model-value="companies.activeId ?? ALL_COMPANIES"
        :options="companyOptions"
        :loading="companies.loading"
        option-label="name"
        option-value="id"
        placeholder="Дружество"
        filter
        filter-placeholder="Търсене"
        empty-message="Няма дружества"
        empty-filter-message="Няма съвпадения"
        class="topbar__company"
        aria-label="Текущо дружество"
        @update:model-value="pickCompany"
      />

      <span class="topbar__user">
        <i class="pi pi-user" aria-hidden="true" />
        {{ auth.displayName }}
      </span>

      <Button
        type="button"
        text
        icon="pi pi-sign-out"
        label="Изход"
        aria-label="Изход от системата"
        @click="signOut"
      />
    </div>
  </header>
</template>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 1.5rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
}

.topbar__tabs {
  display: flex;
  gap: 0.25rem;
  min-width: 0;
  overflow-x: auto;
}

.topbar__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--p-content-border-radius, 6px);
  color: var(--p-text-muted-color);
  text-decoration: none;
  white-space: nowrap;
}

.topbar__tab:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.topbar__tab--active {
  background: var(--p-highlight-background);
  color: var(--p-highlight-color);
  font-weight: 600;
}

.topbar__tab:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: -2px;
}

.topbar__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.topbar__company {
  min-width: 15rem;
}

.topbar__user {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}
@media (max-width: 640px) {
  .topbar { flex-wrap: wrap; padding: .625rem .75rem; gap: .5rem; }
  .topbar__tabs { width: 100%; }
  .topbar__right { width: 100%; min-width: 0; gap: .5rem; }
  .topbar__company { min-width: 0; width: 0; flex: 1; }
  .topbar__user { display: none; }
}
</style>
